import { supabase } from '../lib/supabase';
import { logEvent } from '../lib/activityLogger';
import { withOperationTimeout, withSupabaseQueryTimeout } from '../lib/supabaseTimeout';
import { createId } from '../utils/createId';
import { notificationService } from './notificationService';
import { getDisplayNames } from './memberDisplayService';

const ACTIVE_STATUSES = ['pending', 'accepted', 'denied'];

const normalizeString = (value) => String(value || '').trim();

const toError = (message) => ({ message });

const createApplicationId = () => createId();

const buildCreatedApplication = (payload, now) => ({
  ...payload,
  status: 'pending',
  decision_note: null,
  decided_by: null,
  decided_at: null,
  archived_by: null,
  archived_at: null,
  created_at: now,
  updated_at: now,
});

const insertApplication = async ({
  payload,
  application,
  actorUserId,
  logMetadata = {},
  includeMatchedPortalUserId,
}) => {
  const { error } = await withSupabaseQueryTimeout(
    supabase.from('acc_applications').insert(payload),
    'Submitting ACC application',
    30000
  );

  if (error) return { data: null, error };

  logEvent('acc_application_submitted', {
    application_id: application.id,
    project_title: application.project_title,
    ...logMetadata,
  }).then(null, console.error);

  queueSubmissionNotifications({
    application,
    actorUserId,
    includeMatchedPortalUserId,
  });

  return { data: application, error: null };
};

const getCurrentUser = async () => {
  const { data, error } = await withOperationTimeout(
    supabase.auth.getUser(),
    'Checking your session'
  );
  if (error) return { user: null, error };
  return { user: data?.user || null, error: null };
};

const getCurrentUserOrFallback = async (actorUserId) => {
  if (actorUserId) return { user: { id: actorUserId }, error: null };
  return getCurrentUser();
};

const getProfilesByIds = async (ids) => {
  const uniqueIds = [...new Set((ids || []).filter(Boolean))];
  if (!uniqueIds.length) return {};

  // Display names only, via get_member_display_names (never throws).
  const names = await getDisplayNames(uniqueIds);
  const profiles = {};
  names.forEach((value, id) => { profiles[id] = { id, full_name: value.full_name }; });
  return profiles;
};

export const _resolveACCRecipients = async (
  hoaId,
  { excludeUserId, includeMatchedPortalUserId } = {}
) => {
  if (!hoaId) return [];

  try {
    return await notificationService.getACCRecipientIds({
      hoaId,
      excludeUserId,
      includeUserId: includeMatchedPortalUserId,
    });
  } catch (error) {
    console.error('ACC recipient resolution failed:', error.message);
    return [];
  }
};

export const _insertACCNotifications = async ({
  hoaId,
  recipientUserIds,
  type,
  title,
  body,
  applicationId,
  triggeredByUserId,
}) => {
  try {
    await notificationService.createForUsers({
      hoaId,
      userIds: recipientUserIds,
      type,
      title,
      body,
      actionUrl: `/acc/applications/${applicationId}`,
      triggeredBy: triggeredByUserId,
      relatedEntityType: 'acc_application',
      relatedEntityId: applicationId,
    });
  } catch (error) {
    console.error('ACC notification insert failed:', error.message);
  }
};

export const _sendEmailNotification = async ({ applicationId, eventType, triggeredByUserId }) => {
  try {
    const { data, error } = await withOperationTimeout(
      supabase.functions.invoke('send-acc-notification', {
        body: {
          application_id: applicationId,
          event_type: eventType,
          triggered_by_user_id: triggeredByUserId,
        },
      }),
      'Sending ACC email notification'
    );

    if (error) {
      console.error('ACC email send failed:', error.message || error);
    }
    return data || null;
  } catch (error) {
    console.error('ACC email invoke failed:', error.message || error);
    return null;
  }
};

const queuePortalNotifications = ({
  application,
  recipientUserIds,
  type,
  title,
  body,
  triggeredByUserId,
}) => {
  _insertACCNotifications({
    hoaId: application.hoa_id,
    recipientUserIds,
    type,
    title,
    body,
    applicationId: application.id,
    triggeredByUserId,
  }).then(null, console.error);
};

const queueSubmissionNotifications = ({ application, actorUserId, includeMatchedPortalUserId }) => {
  (async () => {
    const recipients = await _resolveACCRecipients(application.hoa_id, {
      excludeUserId: actorUserId,
      includeMatchedPortalUserId,
    });

    queuePortalNotifications({
      application,
      recipientUserIds: recipients,
      type: 'acc_submission',
      title: `New ACC application: ${application.project_title}`,
      body: `${application.submitter_name || application.submitter_display_name || 'A homeowner'} submitted an ACC application for review.`,
      triggeredByUserId: actorUserId,
    });
  })().then(null, console.error);

  _sendEmailNotification({
    applicationId: application.id,
    eventType: 'submission_received',
    triggeredByUserId: actorUserId,
  }).then(null, console.error);
};

const queueCommentNotifications = ({ application, actorUserId }) => {
  (async () => {
    const recipients = await _resolveACCRecipients(application.hoa_id, {
      excludeUserId: actorUserId,
    });

    queuePortalNotifications({
      application,
      recipientUserIds: recipients,
      type: 'acc_comment',
      title: `New ACC comment: ${application.project_title}`,
      body: `A new internal comment was added to "${application.project_title}".`,
      triggeredByUserId: actorUserId,
    });
  })().then(null, console.error);
};

const queueDecisionNotifications = ({ application, actorUserId, decision }) => {
  (async () => {
    const recipients = await _resolveACCRecipients(application.hoa_id, {
      excludeUserId: actorUserId,
      includeMatchedPortalUserId: application.submitter_user_id,
    });

    queuePortalNotifications({
      application,
      recipientUserIds: recipients,
      type: 'acc_decision',
      title: `ACC application ${decision}: ${application.project_title}`,
      body: `The ACC application "${application.project_title}" was ${decision}.`,
      triggeredByUserId: actorUserId,
    });
  })().then(null, console.error);

  _sendEmailNotification({
    applicationId: application.id,
    eventType: decision === 'accepted' ? 'decision_accepted' : 'decision_denied',
    triggeredByUserId: actorUserId,
  }).then(null, console.error);
};

const decorateApplications = async (applications) => {
  if (!applications?.length) return [];

  const applicationIds = applications.map((application) => application.id);
  const profileIds = applications
    .flatMap((application) => [
      application.submitter_user_id,
      application.created_by,
      application.decided_by,
      application.archived_by,
    ])
    .filter(Boolean);

  const [profilesById, commentCounts] = await Promise.all([
    getProfilesByIds(profileIds),
    getCommentCounts(applicationIds),
  ]);

  return applications.map((application) => {
    const submitterProfile = profilesById[application.submitter_user_id];
    const decidedByProfile = profilesById[application.decided_by];
    const archivedByProfile = profilesById[application.archived_by];

    return {
      ...application,
      submitter_display_name:
        submitterProfile?.full_name ||
        submitterProfile?.email ||
        application.submitter_name ||
        'Unknown submitter',
      decided_by_name: decidedByProfile?.full_name || decidedByProfile?.email || null,
      archived_by_name: archivedByProfile?.full_name || archivedByProfile?.email || null,
      comment_count: commentCounts[application.id] || 0,
    };
  });
};

const getCommentCounts = async (applicationIds) => {
  if (!applicationIds?.length) return {};

  try {
    const { data, error } = await withSupabaseQueryTimeout(
      supabase
        .from('acc_application_comments')
        .select('application_id')
        .in('application_id', applicationIds),
      'Loading ACC comment counts'
    );

    if (error) {
      console.error('accApplicationsService comment count failed:', error.message);
      return {};
    }

    return (data || []).reduce((acc, row) => {
      acc[row.application_id] = (acc[row.application_id] || 0) + 1;
      return acc;
    }, {});
  } catch (error) {
    console.error('accApplicationsService comment count failed:', error.message);
    return {};
  }
};

export const listApplications = async (hoaId, { status } = {}) => {
  if (!hoaId) return { data: [], error: toError('Missing HOA id') };

  try {
    const statuses = Array.isArray(status) ? status : status ? [status] : ACTIVE_STATUSES;
    const buildApplicationsQuery = () => {
      let query = supabase
        .from('acc_applications')
        .select('*')
        .eq('hoa_id', hoaId)
        .order('created_at', { ascending: false });

      if (statuses.length === 1) {
        query = query.eq('status', statuses[0]);
      } else {
        query = query.in('status', statuses);
      }

      return query;
    };

    const { data, error } = await withSupabaseQueryTimeout(
      buildApplicationsQuery,
      'Loading ACC applications'
    );
    if (error) return { data: [], error };

    return { data: await decorateApplications(data || []), error: null };
  } catch (error) {
    return { data: [], error };
  }
};

export const listArchivedApplications = async (hoaId) => {
  return listApplications(hoaId, { status: 'archived' });
};

export const getApplication = async (applicationId) => {
  if (!applicationId) return { data: null, error: toError('Missing application id') };

  try {
    const { data: application, error: applicationError } = await withSupabaseQueryTimeout(
      supabase.from('acc_applications').select('*').eq('id', applicationId).maybeSingle(),
      'Loading ACC application'
    );

    if (applicationError) return { data: null, error: applicationError };
    if (!application) return { data: null, error: toError('Application not found') };

    let commentsData = [];
    try {
      const { data, error: commentsError } = await withSupabaseQueryTimeout(
        supabase
          .from('acc_application_comments')
          .select('*')
          .eq('application_id', applicationId)
          .order('created_at', { ascending: true }),
        'Loading ACC comments'
      );

      if (commentsError) {
        console.warn('ACC comments lookup failed:', commentsError.message);
      } else {
        commentsData = data || [];
      }
    } catch (commentsError) {
      console.warn('ACC comments lookup failed:', commentsError.message);
    }

    const profileIds = [
      application.submitter_user_id,
      application.created_by,
      application.decided_by,
      application.archived_by,
      ...(commentsData || []).map((comment) => comment.author_id),
    ].filter(Boolean);
    const profilesById = await getProfilesByIds(profileIds);
    const submitterProfile = profilesById[application.submitter_user_id];

    const decoratedApplication = {
      ...application,
      submitter_display_name:
        submitterProfile?.full_name ||
        submitterProfile?.email ||
        application.submitter_name ||
        'Unknown submitter',
      decided_by_name:
        profilesById[application.decided_by]?.full_name ||
        profilesById[application.decided_by]?.email ||
        null,
      archived_by_name:
        profilesById[application.archived_by]?.full_name ||
        profilesById[application.archived_by]?.email ||
        null,
    };

    const comments = (commentsData || []).map((comment) => ({
      ...comment,
      author_name:
        profilesById[comment.author_id]?.full_name ||
        profilesById[comment.author_id]?.email ||
        'Unknown author',
    }));

    return { data: { application: decoratedApplication, comments }, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const createApplicationAsSelf = async ({
  hoaId,
  projectTitle,
  projectDescription,
  propertyId,
  propertyAddressSnapshot,
  actorUserId,
}) => {
  try {
    const { user, error: userError } = await getCurrentUserOrFallback(actorUserId);
    if (userError) return { data: null, error: userError };
    if (!user)
      return { data: null, error: toError('You must be signed in to submit an application') };
    if (!propertyId)
      return { data: null, error: toError('Select the property for this ACC application') };

    const now = new Date().toISOString();
    const payload = {
      id: createApplicationId(),
      hoa_id: hoaId,
      project_title: normalizeString(projectTitle),
      project_description: normalizeString(projectDescription),
      submitter_user_id: user.id,
      property_id: propertyId,
      property_address_snapshot: normalizeString(propertyAddressSnapshot) || null,
      created_by: user.id,
      created_at: now,
      updated_at: now,
    };

    const data = buildCreatedApplication(payload, now);

    return await insertApplication({
      payload,
      application: data,
      actorUserId: user.id,
    });
  } catch (error) {
    return { data: null, error };
  }
};

export const createApplicationOnBehalfOf = async ({
  hoaId,
  projectTitle,
  projectDescription,
  submitterName,
  submitterEmail,
  submitterPhone,
  propertyId,
  propertyAddressSnapshot,
  actorUserId,
}) => {
  try {
    const { user, error: userError } = await getCurrentUserOrFallback(actorUserId);
    if (userError) return { data: null, error: userError };
    if (!user)
      return { data: null, error: toError('You must be signed in to submit an application') };
    if (!propertyId)
      return { data: null, error: toError('Select the property for this ACC application') };

    const trimmedEmail = normalizeString(submitterEmail);
    const now = new Date().toISOString();

    const payload = {
      id: createApplicationId(),
      hoa_id: hoaId,
      project_title: normalizeString(projectTitle),
      project_description: normalizeString(projectDescription),
      property_id: propertyId,
      property_address_snapshot: normalizeString(propertyAddressSnapshot) || null,
      created_by: user.id,
      submitter_name: normalizeString(submitterName),
      submitter_email: trimmedEmail,
      submitter_phone: normalizeString(submitterPhone) || null,
      created_at: now,
      updated_at: now,
    };

    const data = buildCreatedApplication(payload, now);

    return await insertApplication({
      payload,
      application: data,
      actorUserId: user.id,
      logMetadata: {
        on_behalf_of: true,
        matched_portal_user: false,
      },
    });
  } catch (error) {
    return { data: null, error };
  }
};

export const addComment = async ({
  applicationId,
  body,
  hoaId,
  applicationStatus,
  projectTitle,
  actorUserId,
}) => {
  try {
    const { user, error: userError } = await getCurrentUserOrFallback(actorUserId);
    if (userError) return { data: null, error: userError };
    if (!user) return { data: null, error: toError('You must be signed in to comment') };

    let application = hoaId
      ? {
          id: applicationId,
          hoa_id: hoaId,
          status: applicationStatus,
          project_title: projectTitle,
        }
      : null;

    if (!application) {
      const { data: loadedApplication, error: applicationError } = await withSupabaseQueryTimeout(
        supabase
          .from('acc_applications')
          .select('id, hoa_id, status, project_title')
          .eq('id', applicationId)
          .maybeSingle(),
        'Loading ACC application'
      );

      if (applicationError) return { data: null, error: applicationError };
      if (!loadedApplication) return { data: null, error: toError('Application not found') };
      application = loadedApplication;
    }

    if (application.status === 'archived')
      return { data: null, error: toError('Archived applications are read-only') };

    const { data, error } = await withSupabaseQueryTimeout(
      supabase
        .from('acc_application_comments')
        .insert({
          application_id: applicationId,
          hoa_id: application.hoa_id,
          author_id: user.id,
          body: normalizeString(body),
        })
        .select('*')
        .single(),
      'Adding ACC comment',
      30000
    );

    if (error) return { data: null, error };

    logEvent('acc_application_comment_added', {
      application_id: applicationId,
      comment_id: data.id,
    }).then(null, console.error);

    queueCommentNotifications({
      application,
      actorUserId: user.id,
    });

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const decideApplication = async ({ applicationId, decision, decisionNote }) => {
  if (!['accepted', 'denied'].includes(decision)) {
    return { data: null, error: toError('Decision must be accepted or denied') };
  }

  try {
    const { user, error: userError } = await getCurrentUser();
    if (userError) return { data: null, error: userError };
    if (!user)
      return { data: null, error: toError('You must be signed in to decide an application') };

    const { data: application, error: applicationError } = await withSupabaseQueryTimeout(
      supabase
        .from('acc_applications')
        .select('id, hoa_id, status, project_title, submitter_user_id')
        .eq('id', applicationId)
        .maybeSingle(),
      'Loading ACC application'
    );

    if (applicationError) return { data: null, error: applicationError };
    if (!application) return { data: null, error: toError('Application not found') };
    if (application.status !== 'pending') {
      return { data: null, error: toError('Only pending applications can receive a decision') };
    }

    const { data, error } = await withSupabaseQueryTimeout(
      supabase
        .from('acc_applications')
        .update({
          status: decision,
          decision_note: normalizeString(decisionNote) || null,
          decided_by: user.id,
          decided_at: new Date().toISOString(),
        })
        .eq('id', applicationId)
        .select('*')
        .single(),
      'Saving ACC decision'
    );

    if (error) return { data: null, error };

    logEvent('acc_application_decided', {
      application_id: applicationId,
      decision,
    }).then(null, console.error);

    queueDecisionNotifications({
      application: data,
      actorUserId: user.id,
      decision,
    });

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const archiveApplication = async (applicationId) => {
  try {
    const { user, error: userError } = await getCurrentUser();
    if (userError) return { data: null, error: userError };
    if (!user)
      return { data: null, error: toError('You must be signed in to archive an application') };

    const { data: application, error: applicationError } = await withSupabaseQueryTimeout(
      supabase.from('acc_applications').select('id, status').eq('id', applicationId).maybeSingle(),
      'Loading ACC application'
    );

    if (applicationError) return { data: null, error: applicationError };
    if (!application) return { data: null, error: toError('Application not found') };
    if (application.status === 'pending') {
      return {
        data: null,
        error: toError('Pending applications must be accepted or denied before archival'),
      };
    }
    if (application.status === 'archived') {
      return { data: null, error: toError('This application is already archived') };
    }

    const { data, error } = await withSupabaseQueryTimeout(
      supabase
        .from('acc_applications')
        .update({
          status: 'archived',
          archived_by: user.id,
          archived_at: new Date().toISOString(),
        })
        .eq('id', applicationId)
        .select('*')
        .single(),
      'Archiving ACC application'
    );

    if (error) return { data: null, error };

    logEvent('acc_application_archived', {
      application_id: applicationId,
    }).then(null, console.error);

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const deleteApplication = async ({ applicationId, actorRole } = {}) => {
  if (actorRole !== 'admin') {
    return { data: null, error: toError('Only administrators can delete ACC applications') };
  }
  if (!applicationId) {
    return { data: null, error: toError('Missing application id') };
  }

  try {
    const { data, error } = await withSupabaseQueryTimeout(
      supabase
        .from('acc_applications')
        .delete()
        .eq('id', applicationId)
        .select('id, project_title')
        .maybeSingle(),
      'Deleting ACC application',
      30000
    );

    if (error) return { data: null, error };
    if (!data) {
      return {
        data: null,
        error: toError(
          'Application could not be deleted. It may already be gone or your session may not have admin access.'
        ),
      };
    }

    logEvent('acc_application_deleted', {
      application_id: data.id,
      project_title: data.project_title,
    }).then(null, console.error);

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const listPropertiesForCurrentUser = async (hoaId) => {
  if (!hoaId) return { data: [], error: toError('Missing HOA id') };
  try {
    const { data, error } = await withSupabaseQueryTimeout(
      supabase
        .from('directory_properties')
        .select('id, hoa_id, situs_address, owner_name_legal, block, lot')
        .eq('hoa_id', hoaId)
        .order('situs_address', { ascending: true })
        .range(0, 299),
      'Loading properties'
    );
    if (error) return { data: [], error };
    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error };
  }
};

export const listHoaProperties = async (hoaId) => {
  if (!hoaId) return { data: [], error: toError('Missing HOA id') };

  try {
    const { data, error } = await withSupabaseQueryTimeout(
      supabase
        .from('properties')
        .select('id, hoa_id, situs_address, member_name, owner_name_legal, block, lot, phase')
        .eq('hoa_id', hoaId)
        .order('situs_address', { ascending: true })
        .range(0, 199),
      'Loading properties'
    );

    if (error) return { data: [], error };
    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error };
  }
};

export const listExternalEmailNotifications = async (applicationId) => {
  if (!applicationId) return { data: [], error: toError('Missing application id') };

  try {
    const { data, error } = await withSupabaseQueryTimeout(
      supabase
        .from('acc_external_notifications')
        .select(
          'id, application_id, recipient_name, recipient_email, event_type, subject, status, resend_message_id, error_message, attempted_at, sent_at, created_at'
        )
        .eq('application_id', applicationId)
        .order('created_at', { ascending: false }),
      'Loading ACC email status'
    );

    if (error) return { data: [], error };
    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error };
  }
};

export const retryExternalEmailNotification = async ({ applicationId, eventType }) => {
  if (!applicationId || !eventType) {
    return { data: null, error: toError('Missing application id or event type') };
  }

  try {
    const { user, error: userError } = await getCurrentUser();
    if (userError) return { data: null, error: userError };
    if (!user)
      return { data: null, error: toError('You must be signed in to retry email delivery') };

    const data = await _sendEmailNotification({
      applicationId,
      eventType,
      triggeredByUserId: user.id,
    });

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

const ACC_BUCKET = 'acc-attachments';               // reuse existing bucket
const ACC_PATH_PREFIX = 'acc-app';                  // namespace within the bucket
export const ACC_ALLOWED_MIME_TYPES = [
  'image/jpeg','image/png','image/webp','image/heic','image/tiff',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
];
export const ACC_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;   // 10 MB per file
export const ACC_MAX_BATCH_SIZE_BYTES = 25 * 1024 * 1024;  // 25 MB per batch

const sanitizeAttachmentName = (name) => String(name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_').substring(0, 200);

export const uploadACCAttachments = async ({ applicationId, hoaId, files }) => {
  try {
    const { user, error: userError } = await getCurrentUser();
    if (userError) return { data: null, error: userError };
    if (!user) return { data: null, error: toError('You must be signed in to upload') };
    if (!applicationId || !hoaId) return { data: null, error: toError('Missing application context') };

    const fileList = Array.from(files || []);
    if (!fileList.length) return { data: [], error: null };

    const totalBytes = fileList.reduce((sum, f) => sum + (f.size || 0), 0);
    if (totalBytes > ACC_MAX_BATCH_SIZE_BYTES) {
      return { data: null, error: toError('Attachments exceed the 25 MB total limit. Remove a file or upload fewer at once.') };
    }

    // Pre-validate every file BEFORE uploading any, so a bad file can't orphan earlier uploads.
    for (const file of fileList) {
      if (!ACC_ALLOWED_MIME_TYPES.includes(file.type)) {
        return { data: null, error: toError(`File type not allowed: ${file.name}`) };
      }
      if (file.size > ACC_MAX_FILE_SIZE_BYTES) {
        return { data: null, error: toError(`File too large (max 10 MB): ${file.name}`) };
      }
    }

    const results = [];
    for (const file of fileList) {
      const safeName = sanitizeAttachmentName(file.name);
      const storagePath = `${ACC_PATH_PREFIX}/${applicationId}/${createId()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from(ACC_BUCKET).upload(storagePath, file, { contentType: file.type });
      if (uploadError) return { data: null, error: uploadError };

      // Magic-byte validation via existing edge function (hard-codes acc-attachments bucket).
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/validate-attachment`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ storage_path: storagePath, declared_mime_type: file.type }),
        }
      );
      if (!res.ok) {
        let msg = 'File validation failed';
        try { const j = await res.json(); msg = j.error || msg; } catch (_) {}
        return { data: null, error: toError(msg) };
      }

      const { data: row, error: metaError } = await withSupabaseQueryTimeout(
        supabase.from('acc_application_attachments').insert({
          application_id: applicationId, hoa_id: hoaId, storage_path: storagePath,
          filename: safeName, mime_type: file.type, size_bytes: file.size, uploaded_by: user.id,
        }).select('*').single(),
        'Saving attachment', 30000
      );
      if (metaError) return { data: null, error: metaError };
      results.push(row);
    }

    logEvent('acc_attachment_uploaded', { application_id: applicationId, count: results.length }).then(null, console.error);
    return { data: results, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const listACCAttachments = async (applicationId) => {
  if (!applicationId) return { data: [], error: toError('Missing application id') };
  try {
    const { data, error } = await withSupabaseQueryTimeout(
      supabase.from('acc_application_attachments')
        .select('id, filename, mime_type, size_bytes, storage_path, created_at')
        .eq('application_id', applicationId)
        .order('created_at', { ascending: true }),
      'Loading attachments'
    );
    if (error) return { data: [], error };
    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error };
  }
};

export const getACCAttachmentUrl = async (storagePath) => {
  try {
    const { data, error } = await supabase.storage.from(ACC_BUCKET).createSignedUrl(storagePath, 900);
    if (error) return { data: null, error };
    return { data: data.signedUrl, error: null };
  } catch (error) {
    return { data: null, error };
  }
};
